import { FC } from 'react'

import { message } from 'antd'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import styled, { useTheme } from 'styled-components'

import { useCreateCampaignLayout } from 'src/Pages/Artist/CreateCampaignPage/useCreateCampaignLayout'
import { PasswordInput } from 'src/Components/PasswordInput'
import { FullWideNotification } from 'src/Components/FullWideNotification'
import { loginEvent } from 'src/Helpers/TagManager'
import { quickLogin } from 'src/Helpers/TagManager/AuthEvents'
import { User } from 'src/Types/users'
import { signIn } from 'src/Redux/auth-process/credentialsSlice/api-actions'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getLoading } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { getAuthEmail } from 'src/Redux/auth-process/authTypeSlice/selectors'
import { ApiError } from 'src/Redux/types'
import { switchToMagicLink } from 'src/Redux/auth-process/authTypeSlice'
import { sendQuickLoginLink } from 'src/Redux/auth-process/emailPasswordSlice/api-actions'
import { useQuickLoginUTMParameters } from 'src/Hooks/useQuickLoginUTMParameters'
import { useSourceBusterEvents } from 'src/Hooks/useSourceBusterEvents'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { TextLinkButton } from 'src/Components/Buttons/TextLinkButton'
import { variables } from 'src/Styled/variables'

import { schemaForRegularLogin } from './schema'

const Title = styled.p`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;

  @media (min-width: 576px) {
    font-size: 28px;
  }
`

const StyledCTAButton = styled(CTAButton)`
  width: 100%;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`

const FooterText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

interface FormState {
  password: string
}

export const RegularLogin: FC = () => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const loading = useAppSelector(getLoading)
  const email = useAppSelector(getAuthEmail)
  const utmParameters = useQuickLoginUTMParameters()
  const [messageApi, contextHolder] = message.useMessage()

  const { resetSearchTracksToInitialState } = useCreateCampaignLayout()
  const { sourceBusterLoginEvent } = useSourceBusterEvents()

  const { control, handleSubmit, setError } = useForm<FormState>({
    resolver: yupResolver(schemaForRegularLogin),
  })

  const onSubmit = ({ password }: FormState): void => {
    void dispatch(signIn({ email, password }))
      .unwrap()
      .then((res) => {
        loginEvent(res.user as User)
        sourceBusterLoginEvent({ token: res.token })
        resetSearchTracksToInitialState()
        return res
      })
      .catch((error: ApiError) => {
        if (
          error.message === 'Curator in blacklist' ||
          error.message === 'Country in blacklist'
        ) {
          void messageApi.open({
            content: (
              <FullWideNotification message='Sorry, our platform is no longer available for users from your country' />
            ),
            style: { backgroundColor: theme.color.mediumRed },
            icon: null,
          })
          return
        }
        setError('password', { message: 'Incorrect password' })
      })
  }

  const onMagicLinkClick = (): void => {
    dispatch(switchToMagicLink())
    void dispatch(sendQuickLoginLink({ email, utmParameters })).then(
      (action) => {
        if (sendQuickLoginLink.fulfilled.match(action)) {
          quickLogin()
        }
      },
    )
  }

  return (
    <>
      {contextHolder}
      <Title>Enter your password</Title>
      <FieldsWrapper as='form' onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='password'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PasswordInput
              {...field}
              placeholder='Password'
              error={error?.message}
              autoFocus
            />
          )}
        />
        <StyledCTAButton type='submit' loading={loading}>
          Continue
        </StyledCTAButton>
      </FieldsWrapper>
      <Footer>
        <FooterText>Forgot password?</FooterText>
        <TextLinkButton type='button' onClick={onMagicLinkClick}>
          Log in with a magic link
        </TextLinkButton>
      </Footer>
    </>
  )
}
